import { trackCall } from "@/lib/track";
import React from "react";
import { Link } from "react-router-dom";
import { Wrench, MapPin, Phone, Mail, Clock } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

const DAY_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// Group consecutive same-hours days into ranges (e.g. "Mon–Fri 08:00 – 17:00")
const formatHours = (hours) => {
  if (!Array.isArray(hours) || hours.length === 0) return [];
  const map = Object.fromEntries(hours.map((h) => [h.day, h]));
  const groups = [];
  let i = 0;
  while (i < DAY_ORDER.length) {
    const d = DAY_ORDER[i];
    const h = map[d];
    if (!h) { i++; continue; }
    const sig = h.closed ? "CLOSED" : `${h.open}-${h.close}`;
    let j = i;
    while (j + 1 < DAY_ORDER.length) {
      const next = map[DAY_ORDER[j + 1]];
      const nextSig = !next ? null : (next.closed ? "CLOSED" : `${next.open}-${next.close}`);
      if (nextSig !== sig) break;
      j++;
    }
    const startLabel = DAY_LABELS[DAY_ORDER[i]].slice(0, 3);
    const endLabel = DAY_LABELS[DAY_ORDER[j]].slice(0, 3);
    const range = i === j ? startLabel : `${startLabel}–${endLabel}`;
    const value = h.closed ? "Closed" : `${h.open} – ${h.close}`;
    groups.push({ range, value });
    i = j + 1;
  }
  return groups;
};

export const Footer = () => {
  const { siteSettings } = useTheme() || {};
  const s = siteSettings || {};
  const phone = s.contact_phone || "(03) 6288 7861";
  const phoneTel = `tel:+${(phone.match(/\d/g) || ["6", "1"]).join("")}`;
  const email = s.contact_email || "hello@tassieautomotive.com.au";
  const address = `${s.address_line1 || "46 Grove Rd"}, ${s.address_line2 || "Glenorchy TAS 7010"}`;
  const addressDirUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  const hourGroups = formatHours(s.opening_hours);

  return (
    <footer data-testid="site-footer" className="bg-zinc-950 border-t border-zinc-900 text-zinc-400 mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-brand-red grid place-items-center">
              <Wrench className="h-5 w-5 text-black" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-2xl uppercase tracking-tight text-white">Tassie Automotive</div>
              <div className="font-mono text-[10px] uppercase text-brand-red tracking-[0.3em]">Est. 2021 · Glenorchy</div>
            </div>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-zinc-400">
            Glenorchy&apos;s trusted Repco Authorised workshop. Professional automotive care for modern and classic
            vehicles — general repairs, electrical diagnostics, tyre service and more.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 border border-brand-red/40 bg-brand-red/10 px-3 py-1.5">
            <span className="h-2 w-2 bg-brand-red rounded-full" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-red">Repco Authorised Service</span>
          </div>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display uppercase tracking-wider text-white text-sm">Workshop</h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/services" className="hover:text-brand-red">Services</Link></li>
            <li><Link to="/about" className="hover:text-brand-red">About</Link></li>
            <li><Link to="/book" className="hover:text-brand-red">Book a service</Link></li>
            <li><Link to="/contact" className="hover:text-brand-red">Contact</Link></li>
            <li><Link to="/login" data-testid="footer-admin-link" className="text-zinc-500 hover:text-brand-red text-xs">Admin login</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-display uppercase tracking-wider text-white text-sm">Hours</h4>
          <ul data-testid="footer-hours" className="mt-4 space-y-2 text-sm font-mono">
            {hourGroups.length === 0 ? (
              <li className="text-zinc-500">Hours not set</li>
            ) : (
              hourGroups.map((g) => (
                <li key={g.range} className="flex justify-between"><span>{g.range}</span><span>{g.value}</span></li>
              ))
            )}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-display uppercase tracking-wider text-white text-sm">Find us</h4>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-brand-red mt-0.5" />
              <a
                href={addressDirUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-directions"
                onClick={() => {
                  if (typeof window !== "undefined" && typeof window.gtag === "function") {
                    window.gtag("event", "conversion", {
                      send_to: "AW-17489509584/lWAaCKGajZkcEND50pNB",
                      value: 1.0,
                      currency: "AUD",
                    });
                  }
                }}
                className="hover:text-brand-red"
              >
                {address}
              </a>
            </li>
            <li className="flex items-center gap-3"><Phone className="h-4 w-4 text-brand-red" /><a href={phoneTel} data-testid="footer-phone" onClick={() => trackCall("footer")} className="hover:text-white">{phone}</a></li>
            <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-brand-red" /><a href={`mailto:${email}`} data-testid="footer-email" className="hover:text-white">{email}</a></li>
            <li className="flex items-center gap-3"><Clock className="h-4 w-4 text-brand-red" /><span>24h dropbox available</span></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-xs font-mono text-zinc-600 uppercase tracking-widest">Areas we service:</span>
          <Link to="/" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Glenorchy</Link>
          <Link data-testid="footer-area-moonah" to="/mechanic/moonah" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Moonah</Link>
          <Link data-testid="footer-area-claremont" to="/mechanic/claremont" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Claremont</Link>
          <Link data-testid="footer-area-montrose" to="/mechanic/montrose" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Montrose</Link>
          <Link data-testid="footer-area-rosetta" to="/mechanic/rosetta" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Rosetta</Link>
          <Link data-testid="footer-area-berriedale" to="/mechanic/berriedale" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Berriedale</Link>
          <Link data-testid="footer-area-new-town" to="/mechanic/new-town" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">New Town</Link>
        </div>
      </div>
      <div className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-xs font-mono text-zinc-600 uppercase tracking-widest">Popular services:</span>
          <Link data-testid="footer-svc-brake-repair" to="/services/brake-repair" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Brake repair</Link>
          <Link data-testid="footer-svc-logbook-servicing" to="/services/logbook-servicing" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Logbook servicing</Link>
          <Link data-testid="footer-svc-car-diagnostics" to="/services/car-diagnostics" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Car diagnostics</Link>
          <Link data-testid="footer-svc-tyres" to="/services/tyres-wheel-alignment" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Tyres &amp; alignment</Link>
          <Link data-testid="footer-svc-aircon" to="/services/air-conditioning" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Air conditioning</Link>
          <Link data-testid="footer-svc-roadworthy" to="/services/roadworthy-certificate" className="text-xs text-zinc-500 hover:text-brand-red transition-colors">Roadworthy certificate</Link>
        </div>
      </div>
      <div className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs font-mono text-zinc-500 uppercase tracking-widest">© {new Date().getFullYear()} Tassie Automotive — All systems calibrated.</p>
          <p className="text-xs font-mono text-zinc-600 uppercase tracking-widest">RWC · LMCT 11892 · ABN 92 123 456 789</p>
        </div>
      </div>
    </footer>
  );
};
